import { ResumeData, ExperienceItem, EducationItem, SkillCategory, CustomSection, CustomSectionItem } from "@/types/resume";

/**
 * Sanitize a filename for cross-platform compatibility.
 * Replaces spaces and special characters with underscores.
 */
export const sanitizeFileName = (name: string): string => {
  return name
    .replace(/[<>:"/\\|?*]/g, '') // Remove invalid file characters
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_+/g, '_') // Collapse multiple underscores
    .replace(/^_|_$/g, '') // Trim leading/trailing underscores
    .trim();
};

/**
 * Extract the first name from a full name string.
 */
export const extractFirstName = (fullName: string): string => {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  return parts[0] || '';
};

/**
 * Extract job title and company from job description text.
 * Uses common patterns to identify role and company names.
 */
export const extractJobInfoFromDescription = (jobDescription: string): { title: string; company: string } => {
  if (!jobDescription || jobDescription.trim().length < 20) {
    return { title: '', company: '' };
  }

  const text = jobDescription.trim();
  let title = '';
  let company = '';

  // Common patterns for job titles (first 500 chars to focus on header)
  const headerText = text.slice(0, 500);
  
  // Pattern 1: "Role at Company" or "Role @ Company"
  const atPattern = headerText.match(/^([A-Za-z][A-Za-z\s&,.-]+?)\s+(?:at|@)\s+([A-Za-z][A-Za-z0-9\s&,.'-]+?)(?:\n|$|–|-|•)/i);
  if (atPattern) {
    title = atPattern[1].trim();
    company = atPattern[2].trim();
  }

  // Pattern 2: Look for common job title keywords
  if (!title) {
    const titlePatterns = [
      /\b((?:Senior|Junior|Lead|Staff|Principal|Associate|Entry.Level|Mid.Level)?\s*(?:Software|Frontend|Backend|Full.Stack|Fullstack|Mobile|Web|Cloud|DevOps|Data|ML|AI|Machine Learning|Product|Project|Program|Engineering|Technical|UX|UI|Marketing|Sales|Business|Operations|HR|Finance|Account|Customer|Support|Quality|QA|Security|Network|System|Database|Solutions|Research|Design|Content|Growth|Revenue|Brand|Digital|Social|Analytics|Cyber|Information|IT)\s*(?:Engineer|Developer|Manager|Analyst|Designer|Architect|Specialist|Consultant|Coordinator|Administrator|Director|Lead|Associate|Executive|Officer|Strategist|Scientist|Researcher|Intern|Trainee))/i,
      /\b(Product Manager|Project Manager|Program Manager|Engineering Manager|Technical Lead|Tech Lead|Team Lead|Scrum Master|Agile Coach|Business Analyst|Data Analyst|Data Scientist|Data Engineer|Software Developer|Software Engineer|Web Developer|Frontend Developer|Backend Developer|Full Stack Developer|DevOps Engineer|Cloud Engineer|ML Engineer|AI Engineer|UX Designer|UI Designer|Product Designer|Graphic Designer|Marketing Manager|Sales Manager|Account Manager|Customer Success|HR Manager|Finance Manager|Operations Manager)\b/i
    ];
    
    for (const pattern of titlePatterns) {
      const match = headerText.match(pattern);
      if (match) {
        title = match[1].trim();
        break;
      }
    }
  }

  // Pattern 3: Look for company name patterns
  if (!company) {
    const companyPatterns = [
      /\b(?:at|@|with|for|join(?:ing)?)\s+([A-Z][A-Za-z0-9\s&,.'-]{1,40}?)(?:\s+is|\s+we|\s+as|\s+–|\s+-|\n|$)/i,
      /^([A-Z][A-Za-z0-9\s&,.'-]{1,30}?)\s+(?:is\s+(?:looking|hiring|seeking)|seeks|looking\s+for)/i,
      /(?:Company|Organization|Employer):\s*([A-Za-z][A-Za-z0-9\s&,.'-]+?)(?:\n|$)/i,
      /About\s+([A-Z][A-Za-z0-9\s&,.'-]{1,30}?)(?:\n|:|$)/i
    ];
    
    for (const pattern of companyPatterns) {
      const match = headerText.match(pattern);
      if (match) {
        company = match[1].trim()
          .replace(/[,.]$/, '') // Remove trailing punctuation
          .replace(/\s+(is|we|as)$/i, ''); // Remove trailing words
        break;
      }
    }
  }

  // Clean up extracted values
  title = title.replace(/[^\w\s-]/g, '').trim();
  company = company.replace(/[^\w\s&-]/g, '').trim();

  // Limit length
  if (title.length > 40) title = title.slice(0, 40).trim();
  if (company.length > 30) company = company.slice(0, 30).trim();

  return { title, company };
};

/**
 * Generate a smart filename for resume downloads.
 * Format: {Position}_{Company}_Resume_{FirstName} or {Position}_Resume_{FirstName}
 * Falls back to: Resume_{FullName}
 */
export const generateSmartFileName = (
  resumeData: ResumeData,
  jobDescription?: string,
  customName?: string
): string => {
  const firstName = extractFirstName(resumeData.personalInfo.fullName);
  
  // If user provided a custom name, use it
  if (customName && customName.trim()) {
    return sanitizeFileName(customName.trim());
  }
  
  // Try to extract job info from job description
  let { title, company } = extractJobInfoFromDescription(jobDescription || '');
  
  // If no job description, try to use most recent experience
  if (!title && resumeData.experience.length > 0) {
    const latestExp = resumeData.experience[0];
    title = latestExp.jobTitle || '';
    if (!company) company = latestExp.company || '';
  }
  
  // Build filename parts
  const parts: string[] = [];
  
  if (title) {
    parts.push(sanitizeFileName(title));
  }
  
  if (company) {
    parts.push(sanitizeFileName(company));
  }
  
  parts.push('Resume');
  
  if (firstName) {
    parts.push(sanitizeFileName(firstName));
  }
  
  // Join with underscores
  const fileName = parts.join('_');
  
  // Fallback if we end up with just "Resume"
  if (fileName === 'Resume' && resumeData.personalInfo.fullName) {
    return sanitizeFileName(`Resume_${resumeData.personalInfo.fullName}`);
  }
  
  return fileName || 'Resume';
};

/**
 * Generate an export filename from explicit user inputs.
 * Format: {Role}_{Company}_Resume_{FirstName}
 */
export const generateExportFileNameFromInputs = (params: {
  role: string;
  company: string;
  applicantFullName: string;
}): string => {
  const role = params.role.trim();
  const company = params.company.trim();
  const firstName = extractFirstName(params.applicantFullName);

  const parts: string[] = [];
  if (role) parts.push(sanitizeFileName(role));
  if (company) parts.push(sanitizeFileName(company));
  parts.push("Resume");
  if (firstName) parts.push(sanitizeFileName(firstName));

  return parts.join("_") || "Resume";
};

/**
 * Sanitize text to remove corrupted characters and HTML entities.
 * Fixes issues like "&AI&" appearing instead of proper text.
 */
export const sanitizeText = (text: string | undefined | null): string => {
  if (!text) return "";
  
  return text
    // Decode common HTML entities
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, ' ')
    // Remove orphaned ampersands that don't form valid entities
    .replace(/&([a-zA-Z0-9]+)?&/g, ' ')
    // Clean up multiple ampersands
    .replace(/&{2,}/g, '&')
    // Remove non-printable characters
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    .trim();
};

export const createEmptyResume = (): ResumeData => ({
  personalInfo: {
    fullName: "",
    email: "",
    phone: "",
    location: "",
    linkedin: "",
    github: "",
    portfolio: "",
  },
  summary: "",
  coreStrengths: [],
  experience: [],
  education: [],
  customSections: [],
  skills: [],
});

export const createEmptyExperience = (): ExperienceItem => ({
  id: crypto.randomUUID(),
  jobTitle: "",
  company: "",
  location: "",
  workType: "",
  startDate: "",
  endDate: "",
  current: false,
  bullets: [""],
});

export const createEmptyEducation = (): EducationItem => ({
  id: crypto.randomUUID(),
  degree: "",
  field: "",
  institution: "",
  location: "",
  graduationDate: "",
  gpa: "",
});

export const createEmptySkillCategory = (): SkillCategory => ({
  id: crypto.randomUUID(),
  category: "",
  skills: "",
});

export const createEmptyCustomSection = (): CustomSection => ({
  id: crypto.randomUUID(),
  title: "",
  items: [],
});

export const createEmptyCustomSectionItem = (): CustomSectionItem => ({
  id: crypto.randomUUID(),
  type: 'item',
  title: "",
  subtitle: "",
  date: "",
  description: "",
  link: "",
  bullets: [""],
});

export const createEmptyBulletItem = (): CustomSectionItem => ({
  id: crypto.randomUUID(),
  type: 'bullet',
  title: "",
  bullets: [],
});

export const formatDate = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString + "-01");
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

export const formatDateFull = (dateString: string | undefined | null): string => {
  if (!dateString || dateString.trim() === "") return "";
  const parts = dateString.split("-");
  if (parts.length < 2) return dateString; // Return as-is if not proper format
  const [year, month] = parts;
  const monthIndex = parseInt(month) - 1;
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) return year || "";
  return `${monthNames[monthIndex]} ${year}`;
};

export const generatePlainTextResume = (data: ResumeData): string => {
  let text = "";
  
  // Header
  text += `${data.personalInfo.fullName.toUpperCase()}\n`;
  const contactParts = [
    data.personalInfo.location,
    data.personalInfo.phone,
    data.personalInfo.email,
    data.personalInfo.github ? `GitHub: ${data.personalInfo.github}` : null,
    data.personalInfo.portfolio ? `Portfolio: ${data.personalInfo.portfolio}` : null,
    data.personalInfo.linkedin,
  ].filter(Boolean);
  text += `${contactParts.join(" | ")}\n\n`;
  
  // Education FIRST
  if (data.education.length > 0) {
    text += `EDUCATION\n`;
    data.education.forEach((edu) => {
      text += `${edu.institution}\t\t${edu.graduationDate ? formatDateFull(edu.graduationDate) : ""}\n`;
      text += `${edu.degree}${edu.field ? ` in ${edu.field}` : ""}`;
      if (edu.gpa) text += ` | GPA: ${edu.gpa}`;
      text += "\n";
    });
    text += "\n";
  }
  
  // Summary
  if (data.summary) {
    text += `SUMMARY\n`;
    text += `${data.summary}\n\n`;
  }
  
  // Core Strengths / Skills
  if (data.coreStrengths && data.coreStrengths.length > 0) {
    text += `CORE STRENGTHS\n`;
    data.coreStrengths.forEach((cat) => {
      if (cat.category && cat.skills) {
        text += `• ${cat.category}: ${cat.skills}\n`;
      }
    });
    text += "\n";
  }
  
  // Experience
  if (data.experience.length > 0) {
    text += `EXPERIENCE\n`;
    data.experience.forEach((exp) => {
      text += `\n${exp.company}\t\t${formatDateFull(exp.startDate)} - ${exp.current ? "Present" : formatDateFull(exp.endDate)}\n`;
      text += `${exp.jobTitle}\t\t${exp.location}${exp.workType ? ` | ${exp.workType}` : ""}\n`;
      exp.bullets.filter(Boolean).forEach((bullet) => {
        text += `• ${bullet}\n`;
      });
    });
    text += "\n";
  }

  // Certifications (custom sections matching certif/licen)
  const certSections = (data.customSections || []).filter((s) => /certif|licen/i.test(s.title || ""));
  certSections.forEach((section) => {
    if (section.title) {
      text += `\n${section.title.toUpperCase()}\n`;
      section.items.forEach((item) => {
        if (item.title) {
          text += `${item.title}${item.date ? `\t\t${item.date}` : ""}\n`;
          if (item.subtitle) text += `${item.subtitle}\n`;
          if (item.description) text += `${item.description}\n`;
          item.bullets.filter(Boolean).forEach((bullet) => {
            text += `• ${bullet}\n`;
          });
        }
      });
    }
  });

  // Projects (custom sections matching project)
  const projSections = (data.customSections || []).filter((s) => /project/i.test(s.title || ""));
  projSections.forEach((section) => {
    if (section.title) {
      text += `\n${section.title.toUpperCase()}\n`;
      section.items.forEach((item) => {
        if (item.title) {
          text += `${item.title}${item.date ? `\t\t${item.date}` : ""}\n`;
          if (item.subtitle) text += `${item.subtitle}\n`;
          if (item.description) text += `${item.description}\n`;
          item.bullets.filter(Boolean).forEach((bullet) => {
            text += `• ${bullet}\n`;
          });
        }
      });
    }
  });

  // Other Custom Sections
  const otherSections = (data.customSections || []).filter((s) => s.title && !/certif|licen|project/i.test(s.title));
  otherSections.forEach((section) => {
    text += `\n${section.title.toUpperCase()}\n`;
    section.items.forEach((item) => {
      if (item.title) {
        text += `${item.title}${item.date ? `\t\t${item.date}` : ""}\n`;
        if (item.subtitle) text += `${item.subtitle}\n`;
        if (item.description) text += `${item.description}\n`;
        item.bullets.filter(Boolean).forEach((bullet) => {
          text += `• ${bullet}\n`;
        });
      }
    });
  });
  
  return text;
};
